import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { addressColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import {
    type ApiAddress,
    type ApiContact,
    ContactLinkLists,
    emailCategoryOptions,
    EnumEmailCategory,
    EnumFrontendLayoutSetting,
} from '@Model';
import {
    Card,
    Checkbox,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    isNullOrEmpty,
    MultiSelect,
    type Nullable,
    type SelectOption,
    SingleSelect,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedList,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';
import { FrameEditAddress } from './FrameEditAddress';

const formsOfAddress: SelectOption<Nullable<number>>[] = [
    { name: 'Firma', label: 'Firma', value: 0 },
    { name: 'Herr', label: 'Herr', value: 1 },
    { name: 'Frau', label: 'Frau', value: 2 },
];

export function FrameEditContact(props: IFrameEditShellProps<ApiContact>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiContact>
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            apiResourcePath={`${STAMMDATEN_API}/contacts`}
            renderer={FrameEditContactBody}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenContactUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenContactCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenContactDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditContactBody(props: IFrameBodyProps<ApiContact>) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const apiContact = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;

    const canEdit = props.mode !== EditFrameMode.View;

    const binder = useGuiBinder<ApiContact>({
        ...props,
        canEdit,
    });

    const {
        refetchRef: refetchAddressesRef,
        linkListApi: linkAddressApi,
        linkedObjectOverlayEvents: linkAddressOverlayEvents,
    } = useLinkedList<ApiContact, ApiAddress>(
        baseApiObjectPath,
        ContactLinkLists.addresses,
        canEdit && hasPermission(Permission.StammdatenAddressesCreate),
        FrameEditAddress,
    );

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Ansprechpartner', props.mode),
                resourceLabel: 'Ansprechpartner',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="Kopfdaten" border={'withoutBorder'} label="Allgemeine Informationen">
                <Checkbox {...binder.bindToAutoConfirmed<boolean>('is_main_contact')} label={i18n('Hauptkontakt')} />
                <Grid columns={8}>
                    <GridColumns spans={[2, 3, 3]}>
                        <SingleSelect
                            items={formsOfAddress}
                            {...binder.bindToAutoConfirmed<Nullable<number>>('form_of_address')}
                            label={i18n('Anrede')}
                            fullWidth
                        />
                        <TextInput {...binder.bindToProp<string>('first_name')} label={i18n('Vorname')} />
                        <TextInput {...binder.bindToProp<string>('last_name')} label={i18n('Nachname')} />
                    </GridColumns>
                </Grid>
                <TextInput type="email" {...binder.bindToProp<string>('email')} label={i18n('Email')} />
            </Card>
            <Card name="Telefonnummern" label="Telefonnummern">
                <Grid>
                    <GridColumns spans={[6, 6]}>
                        <TextInput type="tel" {...binder.bindToProp<string>('phone')} label={i18n('Telefonnummer 1')} />
                        <TextInput type="tel" {...binder.bindToProp<string>('mobile')} label={i18n('Handynummer')} />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="Verknüpfte Adressen" label="Adressen" maxWidth={'80rem'}>
                <LinkedListWithLayoutSettings<ApiAddress>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    layoutSettingsModalContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    {...binder.bindToLinkList(linkAddressApi)}
                    {...linkAddressOverlayEvents}
                    refetchRef={refetchAddressesRef}
                    columns={addressColumns}
                />
            </Card>
            {!isNullOrEmpty(apiContact.email) && (
                <Card name={'Settings'} label={'Einstellungen'}>
                    <Grid>
                        <GridColumn span={6}>
                            <MultiSelect
                                items={emailCategoryOptions}
                                {...binder.bindToAutoConfirmed<EnumEmailCategory[]>('email_categories')}
                                name={i18n('EmailCategories')}
                                label={i18n('Kontaktperson für Emailkategorien')}
                                values={apiContact.email_categories}
                            />
                        </GridColumn>
                    </Grid>
                </Card>
            )}
        </EditFrameShell>
    );
}
